import { useState } from 'react';
import { Card } from '../../components/Card';
import { IconButton } from '../../components/ui';
import { MediaAddForm } from './MediaAddForm';
import { MediaRow } from './MediaRow';
import { useMedia } from './useMedia';

/**
 * 🎬 Media Tracker.
 *
 * Title + release date + type, persisted forever (never a daily reset), bold
 * on the release date, struck through from the day after, and deletable at
 * any time. Zero API, zero keys.
 *
 * Nothing here filters by release status: a shipped item keeps its struck-out
 * row across reloads until the user presses ✕. The only removal path in the
 * whole panel is `remove`.
 */
export function MediaPanel() {
  const { todayKey, items, add, remove } = useMedia();
  const [formOpen, setFormOpen] = useState(false);

  return (
    <Card
      title="Media Tracker"
      icon="🎬"
      accent="media"
      testId="media-panel"
      scroll
      status={items.length === 0 && !formOpen ? 'empty' : 'ready'}
      emptyMessage="Nothing on the watchlist."
      hint="Track a game, anime, movie — anything with a date you're waiting on."
      stateAction={
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-panel-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
        >
          Track something
        </button>
      }
      action={
        <IconButton
          label={formOpen ? 'Close add form' : 'Add tracked item'}
          aria-expanded={formOpen}
          onClick={() => setFormOpen((open) => !open)}
          className={formOpen ? 'bg-panel-raised text-ink' : undefined}
        >
          <span aria-hidden className="text-base leading-none">
            {formOpen ? '✕' : '+'}
          </span>
        </IconButton>
      }
      footer={
        formOpen ? (
          <MediaAddForm onAdd={add} onClose={() => setFormOpen(false)} />
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <p className="pt-2 text-center text-xs text-ink-subtle">
          Add a title below — the date is optional.
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5 pt-0.5">
          {items.map((item) => (
            <MediaRow key={item.id} item={item} todayKey={todayKey} onRemove={remove} />
          ))}
        </ul>
      )}
    </Card>
  );
}
